// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabaseClient';
import InviteDialog from './InviteDialog';
import GroupChatPanel from './GroupChatPanel';

interface Member {
  user_id: string;
  role: string;
  status: string;
}

interface GroupInfo {
  id: string;
  name: string;
  creator_id: string;
}

const GroupDashboard: React.FC = () => {
  const { groupId } = useParams();
  const [group, setGroup] = useState<GroupInfo | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [invites, setInvites] = useState<any[]>([]);
  const [showInvite, setShowInvite] = useState(false);
  const [tab, setTab] = useState<'overview' | 'chat'>('overview');
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    if (!groupId) return;
    fetchGroup();
    fetchMembers();
    fetchInvites();

    const ch = supabase
      .channel(`group-${groupId}-members`)
      .on('postgres_changes', { schema: 'public', table: 'group_members', filter: `group_id=eq.${groupId}` }, () => fetchMembers())
      .on('postgres_changes', { schema: 'public', table: 'invites', filter: `group_id=eq.${groupId}` }, () => fetchInvites())
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [groupId]);

  useEffect(() => {
    supabase.auth.getUser().then(res => setCurrentUserId(res.data.user?.id || null));
  }, []);

  const fetchGroup = async () => {
    const { data, error } = await supabase.from('groups').select('*').eq('id', groupId).single();
    if (!error) setGroup(data as GroupInfo);
  };

  const fetchMembers = async () => {
    const { data } = await supabase.from('group_members').select('*').eq('group_id', groupId);
    setMembers(data as Member[]);
  };

  const fetchInvites = async () => {
    const { data } = await supabase.from('invites').select('*').eq('group_id', groupId);
    setInvites(data || []);
  };

  const approveMember = async (userId: string) => {
    await supabase
      .from('group_members')
      .update({ status: 'active' })
      .eq('group_id', groupId)
      .eq('user_id', userId);
    fetchMembers();
  };

  if (!group) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 flex justify-center">
      <div className="w-full max-w-3xl bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">{group.name}</h1>
          <button onClick={() => setShowInvite(true)} className="px-3 py-2 bg-blue-600 text-white rounded text-sm">
            Invite Members
          </button>
        </div>
        <div className="flex gap-4 border-b mb-4">
          <button className={tab==='overview' ? 'border-b-2 border-blue-600 pb-1' : 'pb-1'} onClick={()=>setTab('overview')}>Overview</button>
          <button className={tab==='chat' ? 'border-b-2 border-blue-600 pb-1' : 'pb-1'} onClick={()=>setTab('chat')}>Chat</button>
        </div>

        {tab==='overview' && (
          <>
            <h2 className="font-semibold">Members</h2>
            <ul className="space-y-1">
              {members.map(m => (
                <li key={m.user_id} className="border rounded p-2 text-sm flex items-center justify-between">
                  <span>{m.user_id.substring(0,6)}… – {m.role} ({m.status})</span>
                  {group.creator_id===currentUserId && m.status==='pending' && (
                    <button onClick={()=>approveMember(m.user_id)} className="text-xs px-2 py-0.5 bg-green-600 text-white rounded">Approve</button>
                  )}
                </li>
              ))}
            </ul>

            {invites.length>0 && (
              <>
                <h3 className="font-semibold mt-4">Invites Pending</h3>
                <ul className="space-y-1">
                  {invites.map(inv => (
                    <li key={inv.email} className="border rounded p-2 text-sm flex items-center justify-between opacity-60 italic">
                      {inv.email} – pending
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}

        {tab==='chat' && <GroupChatPanel groupId={groupId!} />}

        {showInvite && <InviteDialog groupId={groupId!} onClose={() => setShowInvite(false)} />}
      </div>
    </div>
  );
};

export default GroupDashboard; 